import { BDPageSpinner } from '@brightdrop/bd-ui';
import { datadogRum } from '@datadog/browser-rum';
import { Component, type PropsWithChildren, type ReactNode } from 'react';

import {
  hasReloadAttempt,
  isChunkError,
  markReloadAttempt,
} from '~/common/utils/retryDynamicImport';

type Props = { hasError?: boolean };

type State = {
  error: Error | null;
  // Set once we know a chunk error is not recoverable (already reloaded, or the
  // reload guard could not be persisted) so render() stops showing the spinner
  // and hands off to the app-level ErrorFallback boundary.
  giveUp: boolean;
};

class RouteErrorBoundary extends Component<PropsWithChildren<Props>, State> {
  constructor(props: PropsWithChildren<Props>) {
    super(props);
    this.state = { error: null, giveUp: false };
  }

  // Keep this pure: capture the error so render() can decide how to handle it.
  static getDerivedStateFromError(error: Error): State {
    return { error, giveUp: false };
  }

  componentDidCatch(error: Error) {
    // Non-chunk errors are re-thrown by render(); nothing to do here.
    if (!isChunkError(error)) {
      return;
    }

    // Already reloaded once this session and the chunk still failed, so it is
    // gone rather than stale. Hand off to ErrorFallback instead of looping.
    if (hasReloadAttempt()) {
      this.setState({ giveUp: true });
      return;
    }

    // Only reload when the guard is durably persisted. If storage is
    // unavailable, markReloadAttempt() returns false and we give up rather than
    // risk an unguarded reload loop.
    if (!markReloadAttempt()) {
      this.setState({ giveUp: true });
      return;
    }

    datadogRum.addError(error, {
      source: 'custom',
      errorBoundary: 'RouteErrorBoundary',
      errorType: 'chunk_load_failure',
      viewPath: window.location.pathname,
      willReload: true,
    });
    console.warn('Error loading chunk, forcing window reload', error);
    window.location.reload();
  }

  render(): ReactNode {
    const { error, giveUp } = this.state;

    if (!error) {
      return this.props.children;
    }

    // A stale-cache chunk failure we can still recover from: show a spinner
    // while componentDidCatch triggers the one-time reload. `giveUp` covers the
    // cases componentDidCatch decided not to reload (storage unavailable, or a
    // chunk still failing after an earlier reload).
    if (isChunkError(error) && !hasReloadAttempt() && !giveUp) {
      return <BDPageSpinner />;
    }

    // Non-chunk errors, or a chunk we cannot recover from, propagate to the
    // app-level ErrorFallback boundary rather than spinning forever. We do not
    // emit a Datadog RUM event for this case: rethrowing means componentDidCatch
    // bails out before logging, and ErrorFallback already records its own
    // "Temporarily Unavailable fallback rendered" action when it mounts, so
    // logging here would only duplicate that signal.
    throw error;
  }
}

export default RouteErrorBoundary;
